import { useApplication } from "@/context/app";
import { current, close, save } from "../requests/checkup";
import { checkupSave as saveDiagnosis } from "../requests/diagnosis";
import { checkupSave as saveTherapy } from "../requests/therapy";
import { checkupSave as saveComplaint } from "../requests/complaint";
import { checkupSave as saveTest } from "../requests/test";
import { checkupSave as saveSession } from "../requests/session";
import { checkupSave as saveCase } from "../requests/case";
import { list as getSessions } from "../requests/session";
import { createSignal } from "solid-js";
import usePrescriptionPrint from "./usePrescriptionPrint";

export default function useCheckup() {

  const [loading, setLoading] = createSignal(false);
  const [fetching, setFetching] = createSignal(false);
  const [appointment, setAppointment] = createSignal(null);
  const [apppointmentCase, setCase] = createSignal(null);
  const [patient, setPatient] = createSignal(null);
  const [doctor, setDoctor] = createSignal(null);
  const [diagnoses, setDiagnoses] = createSignal([]);
  const [therapies, setTherapies] = createSignal([]);
  const [sessions, setSessions] = createSignal([]);
  const [complaints, setComplaints] = createSignal([]);
  const [tests, setTests] = createSignal([]);
  const [histories, setHistories] = createSignal([]);
  
  // Form data refs for saving
  const [formDataRefs, setFormDataRefs] = createSignal({});

  const { successToast, errorToast } = useApplication();
  const { printPrescription: printPrescriptionService, loading: prescriptionLoading } = usePrescriptionPrint();

  const onClose = async () => {
    setLoading(true);
    try {
      const data = await close({ data: [appointment()?.uid] });

      if (data.success) {
        successToast(data.message);
        await onFetchAppointment();
        return;
      }

      errorToast(data.messages);
    } catch(error) {
      errorToast(error);
    } finally {
      setLoading(false);
    }
  }

  const onFetchAppointment = async () => {
    setFetching(true);
    try {
      const {response,status} = await current();

      if (response.success) {
        setCase(response.case);
        setDiagnoses(response.diagnoses);
        setTherapies(response.therapies);
        setComplaints(response.complaints);
        setSessions(response.sessions);
        setTests(response.tests);
        setHistories(response.histories);
        setAppointment(response.appointment);
        setPatient(response.patient);
        setDoctor(response.doctor);
      } else {
        errorToast(response.message || "Failed to fetch current appointment");
      }
    } catch(error) {
      console.log(error);
      errorToast("Failed to fetch current appointment details");
    } finally {
      setFetching(false);
    }
  }

  const onAction = async (action) => {
    if (action === 'close') {
      await onClose();
    }
  }

  const onSave = async (allFormData) => {
    setLoading(true);
    try {
      const errors = [];
      const successes = [];

      // Save appointment notes
      if (allFormData.appointment && allFormData.appointment.notes !== undefined) {
        try {
          const data = await save({ notes: allFormData.appointment.notes });
          if (data.success) {
            successes.push("Appointment notes saved");
          } else {
            errors.push(data.message || "Failed to save appointment notes");
          }
        } catch (error) {
          errors.push("Failed to save appointment notes");
        }
      }

      // Save case
      if (allFormData.case && Object.keys(allFormData.case).length > 0) {
        try {
          const formData = new FormData();
          formData.append('title', allFormData.case.title || '');
          formData.append('notes', allFormData.case.notes || '');
          const data = await saveCase(formData);
          if (data.success) {
            successes.push("Case saved");
          } else {
            errors.push(data.message || "Failed to save case");
          }
        } catch (error) {
          errors.push("Failed to save case");
        }
      }

      // Save diagnosis
      if (allFormData.diagnosis && allFormData.diagnosis.length > 0) {
        try {
          const formData = new FormData();
          formData.append('diagnoses', JSON.stringify(allFormData.diagnosis));
          const data = await saveDiagnosis(formData);
          if (data.success) {
            successes.push("Diagnosis saved");
          } else {
            errors.push(data.message || "Failed to save diagnosis");
          }
        } catch (error) {
          errors.push("Failed to save diagnosis");
        }
      }

      // Save therapies
      if (allFormData.therapies && allFormData.therapies.length > 0) {
        try {
          const formData = new FormData();
          formData.append('therapies', JSON.stringify(allFormData.therapies));
          const data = await saveTherapy(formData);
          if (data.success) {
            successes.push("Therapies saved");
          } else {
            errors.push(data.message || "Failed to save therapies");
          }
        } catch (error) {
          errors.push("Failed to save therapies");
        }
      }

      // Save complaints
      if (allFormData.complaints && allFormData.complaints.length > 0) {
        try {
          const formData = new FormData();
          formData.append('complaints', JSON.stringify(allFormData.complaints));
          const data = await saveComplaint(formData);
          if (data.success) {
            successes.push("Complaints saved");
          } else {
            errors.push(data.message || "Failed to save complaints");
          }
        } catch (error) {
          errors.push("Failed to save complaints");
        }
      }

      // Save tests
      if (allFormData.tests && allFormData.tests.length > 0) {
        try {
          const formData = new FormData();
          formData.append('tests', JSON.stringify(allFormData.tests));
          const data = await saveTest(formData);
          if (data.success) {
            successes.push("Tests saved");
          } else {
            errors.push(data.message || "Failed to save tests");
          }
        } catch (error) {
          errors.push("Failed to save tests");
        }
      }

      // Save sessions
      if (allFormData.sessions && allFormData.sessions.length > 0) {
        try {
          const formData = new FormData();
          formData.append('sessions', JSON.stringify(allFormData.sessions));
          const data = await saveSession(formData);
          if (data.success) {
            successes.push("Sessions saved");
          } else {
            errors.push(data.message || "Failed to save sessions");
          }
        } catch (error) {
          errors.push("Failed to save sessions");
        }
      }

      // Show results
      if (successes.length > 0) {
        successToast(successes.join(", "));
      }
      if (errors.length > 0) {
        errorToast(errors.join(", "));
      }

      if (errors.length === 0) {
        await onFetchAppointment(); // Refresh data
      }
    } catch (error) {
      errorToast("Failed to save data");
    } finally {
      setLoading(false);
    }
  }

  const onUpdateNote = async (note) => {
    setAppointment('notes', note);
  }

  const onPrintPrescription = async () => {
    await printPrescriptionService({
      appointment: appointment(),
      histories: histories(),
      diagnoses: diagnoses(),
      therapies: therapies(),
      sessions: sessions(),
      complaints: complaints(),
      tests: tests(),
      patient: patient(),
      doctor: doctor(),
    });
  }

  return {
    loading,
    setLoading,
    fetching,
    appointment,
    diagnoses,
    therapies,
    sessions,
    complaints,
    tests,
    histories,
    apppointmentCase,
    onAction,
    onFetchAppointment,
    onSave,
    onUpdateNote,
    onPrintPrescription,
    prescriptionLoading,
  };
}
